import { motion } from "motion/react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import type { TooltipRenderProps } from "react-joyride";

import { Button } from "@/components/ui/v2/button";
import { CaptainAvatar } from "@/features/captain/components/CaptainAvatar";
import { cn } from "@/lib/utils/cn";

const COACHMARK_TRANSITION = { type: "spring", bounce: 0, duration: 0.3 } as const;
const ENTER_OFFSET_PX = 6;

type TourProgressDotsProps = {
  count: number;
  lit: number;
  label: string;
};

function TourProgressDots({ count, lit, label }: TourProgressDotsProps) {
  return (
    <ol aria-label={label} className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
      {Array.from({ length: count }, (_, position) => (
        <motion.li
          key={position}
          aria-current={position === lit - 1 ? "step" : undefined}
          animate={{ scale: position === lit - 1 ? 1.3 : 1 }}
          transition={COACHMARK_TRANSITION}
          className={cn("size-1.5 rounded-full", position < lit ? "bg-primary" : "bg-muted-foreground")}
        />
      ))}
    </ol>
  );
}

export function TourCoachmark({
  index,
  size,
  step,
  backProps,
  primaryProps,
  skipProps,
  tooltipProps,
}: TooltipRenderProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const counterId = useId();
  const bodyId = useId();
  const stepCount = size - 1;
  const isFinish = index === stepCount;
  const isLastStep = index === stepCount - 1;
  const fromBelow = step.placement.startsWith("bottom");

  const primaryLabel = (() => {
    if (isFinish) return t("tour.done");
    if (isLastStep) return t("tour.finish");
    return t("tour.next");
  })();

  return (
    <motion.div
      {...tooltipProps}
      aria-labelledby={titleId}
      aria-describedby={`${counterId} ${bodyId}`}
      initial={{ opacity: 0, y: fromBelow ? -ENTER_OFFSET_PX : ENTER_OFFSET_PX }}
      animate={{ opacity: 1, y: 0 }}
      transition={COACHMARK_TRANSITION}
      className="pointer-events-auto flex w-[min(22.5rem,calc(100vw-2rem))] flex-col gap-3 rounded-xl border bg-popover px-5 pb-4 pt-5 text-popover-foreground shadow-md"
    >
      <div className="flex h-7 items-center gap-2.5">
        <CaptainAvatar className="size-7" />
        <span id={counterId} className="text-xs font-semibold [color:var(--badge-info-text)]">
          {isFinish ? t("tour.complete") : t("tour.stepOf", { current: index + 1, total: stepCount })}
        </span>
        <div className="flex-1" />
        {!isFinish && (
          <Button
            variant="link"
            size="sm"
            data-action={skipProps["data-action"]}
            aria-keyshortcuts="Escape"
            onClick={skipProps.onClick}
            className="h-auto px-1 py-1.5 text-xs font-medium text-muted-foreground underline pointer-coarse:min-h-11"
          >
            {t("tour.skip")}
          </Button>
        )}
      </div>
      <h2 id={titleId} className="text-base font-semibold leading-5">
        {step.title}
      </h2>
      <div id={bodyId} className="text-sm leading-normal">
        {step.content}
      </div>
      {index === 0 && (
        <p className="text-xs text-muted-foreground pointer-coarse:hidden">{t("tour.keyboardHint")}</p>
      )}
      <div className="flex items-center gap-2 pt-1">
        <TourProgressDots
          count={stepCount}
          lit={isFinish ? stepCount : index + 1}
          label={t("tour.progress")}
        />
        {index > 0 && !isFinish && (
          <Button
            variant="outline"
            size="sm"
            data-action={backProps["data-action"]}
            aria-keyshortcuts="ArrowLeft"
            onClick={backProps.onClick}
            className="pointer-coarse:h-11"
          >
            {t("tour.back")}
          </Button>
        )}
        <Button
          size="sm"
          data-action={primaryProps["data-action"]}
          aria-keyshortcuts={isFinish ? "Escape" : "ArrowRight"}
          onClick={primaryProps.onClick}
          className="pointer-coarse:h-11"
        >
          {primaryLabel}
        </Button>
      </div>
    </motion.div>
  );
}
